<template>
  <ExcursionFilterPopupWrapper
    is-fullscreen
    show-close-button
    :title="t('title.header')"
    filter-key="modal"
    :is-disabled-reset-button="isDisabledResetButton"
    is-modal-only
    :modal-size="modalSize"
    modal-class="modal-filters"
    @close="closeModal"
    @open="openModalContent"
    @apply="handleApply"
  >
    <template #trigger="{ open }">
      <div class="filters-open-btn">
        <SkeletonBlock
          :is-active="isSkeletonActive"
          :width="135"
          :height="48"
        >
          <UiButton
            v-if="!isSkeletonActive"
            :text="t('title.header')"
            :variant="UiButtonVariant.OUTLINE"
            :color-mode="UiButtonColorMode.NEUTRAL"
            :size="currentSize"
            @clicked="open"
          >
            <template #icon><ModalFilterBtnIcon /></template>
          </UiButton>
          <div
            v-if="!isSkeletonActive && filtersCount"
            class="filters-count"
          >
            {{ filtersCount }}
          </div>
        </SkeletonBlock>
      </div>
    </template>

    <ExcursionModalFilterContent
      v-if="isFilterContentLoaded"
      :aggregation-movement-type="props.aggregationMovementType"
      :display-currency="props.displayCurrency"
      :is-price-loading="props.isPriceLoading"
      :is-close-modal-trigger="isCloseModalTrigger"
      :price="props.price"
      @display-currency-change="emit('display-currency-change', $event)"
    />
  </ExcursionFilterPopupWrapper>
</template>

<script setup lang="ts">
  import { defineAsyncComponent } from 'vue';
  import { UiModalSize } from '@experience/core-modal-ui';
  import { ExcursionFilterPopupWrapper } from '@/entities/filter/popup-wrapper';
  import { UiButton, UiButtonColorMode, UiButtonSize, UiButtonVariant } from '@experience/core-button-ui';
  import { ModalFilterBtnIcon } from '@/shared/ui/icons';
  import { useI18n } from '@experience/i18n';
  import ru from '../../i18n/ru.json';
  import { useExcursionFiltersStore } from '@/entities/filter/store/excursion';
  import type { IExperienceAggregations } from '@/entities/experience/aggregation';
  import type { TExcursionFiltersState } from '@/entities/filter/store/types/excursion/state.ts';
  import { Sort, type TDisplayCurrencyData } from '@/widgets/filtersBlock/types';
  import { SkeletonBlock } from '@experience/core-skeleton-lib';
  import { useScreenSize } from '@experience/shared';
  import { useExcursionListingSkeleton } from '@/shared/composables/excursion/useExcursionDelayedListingSkeleton.ts';
  import { useDisplayCurrencyFilterSogu } from '@/widgets/filtersBlock/composables/useDisplayCurrencyFilterSogu.ts';

  /**
   * Лениво загружает содержимое модалки со всеми фильтрами после первого открытия.
   */
  const ExcursionModalFilterContent = defineAsyncComponent(() => import('./ExcursionModalFilterContent.vue'));

  interface Props {
    isScroll: boolean;
    aggregationMovementType: IExperienceAggregations['movementType'];
    price: {
      histogram: Record<string, number>;
      minPrice: number;
      maxPrice: number;
    };
    displayCurrency: TDisplayCurrencyData;
    isPriceLoading: boolean;
    onOpen?: (isForced: boolean) => void;
  }

  interface Emits {
    (e: 'apply', isForced: boolean, filterData?: Partial<TExcursionFiltersState>): void;
    (e: 'display-currency-change', isoCode: string): void;
  }

  const emit = defineEmits<Emits>();

  const { isSkeletonActive } = useExcursionListingSkeleton();

  const props = defineProps<Props>();

  const { trackDisplayCurrencyFilterView } = useDisplayCurrencyFilterSogu();

  const { isMobile } = useScreenSize();

  const modalSize = computed(() => (isMobile.value ? UiModalSize.FULLSCREEN : UiModalSize.S));

  const filtersStore = useExcursionFiltersStore();
  const isFilterContentLoaded = ref(false);

  const countCortegeFilter = (value: Array<string | number | null>) => {
    return value?.some((item) => item !== null) ? 1 : 0;
  };

  const countArrayFilter = (value: Array<string>) => {
    return value?.length ? 1 : 0;
  };

  const countBooleanFilter = (value: boolean | null) => {
    return value ? 1 : 0;
  };

  function countAppliedFilters(state: TExcursionFiltersState) {
    let count = 0;

    Object.entries(state).forEach(([key, value]) => {
      switch (key) {
        case 'price':
        case 'duration':
          count += countCortegeFilter(value as Array<string | null | number>);
          break;
        case 'startTimeInterval':
        case 'durationInterval':
        case 'movementTypes':
        case 'type':
          count += countArrayFilter(value as Array<string>);
          break;
        case 'childrenAllowed':
          count += countBooleanFilter(value as boolean);
          break;
        default:
          count += 0;
      }
    });

    return count;
  }

  const filtersCount = computed(() => {
    return countAppliedFilters(filtersStore.searchState);
  });

  const filtersSelectedCount = computed(() => {
    return countAppliedFilters(filtersStore.state);
  });

  const isDisabledResetButton = computed(() => {
    return (
      filtersSelectedCount.value < 1 &&
      filtersCount.value < 1 &&
      (!filtersStore.state.sorting || filtersStore.state.sorting === Sort.popularity)
    );
  });

  const { t } = useI18n({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  const currentSize = computed(() => {
    if (isMobile.value) {
      return UiButtonSize.M;
    }

    return props.isScroll ? UiButtonSize.S : UiButtonSize.M;
  });

  const isCloseModalTrigger = ref(true);

  /**
   * Разрешает загрузку тяжелого содержимого модалки только после открытия
   * и трекает просмотр фильтра валюты.
   */
  const openModalContent = (isForced: boolean) => {
    isFilterContentLoaded.value = true;
    props.onOpen?.(isForced);
    trackDisplayCurrencyFilterView(props.displayCurrency.iso ?? '');
  };

  const closeModal = () => {
    isCloseModalTrigger.value = !isCloseModalTrigger.value;
  };

  const handleApply = (isForced: boolean) => {
    const filterData: Partial<TExcursionFiltersState> = {
      price: filtersStore.searchState.price,
      type: filtersStore.searchState.type,
      movementTypes: filtersStore.searchState.movementTypes,
      sorting: filtersStore.searchState.sorting,
      duration: filtersStore.searchState.duration,
      childrenAllowed: filtersStore.searchState.childrenAllowed,
      durationInterval: filtersStore.searchState.durationInterval,
      startTimeInterval: filtersStore.searchState.startTimeInterval,
    };

    emit('apply', isForced, filterData);
    closeModal();
  };
</script>

<style scoped lang="scss">
  @use '@experience/design-tokens/typography' as typography;
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .filters-open-btn {
    position: relative;
    display: flex;
    align-items: center;

    :deep(.button_size-m) {
      @include adaptivity.mobile {
        padding: 10px 16px 10px 12px;
      }
    }
  }

  .filters-count {
    position: absolute;
    top: -6px;
    right: -6px;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 20px;
    height: 20px;
    border: 2px solid var(--comp-badge-color-border-default);
    border-radius: var(--alias-radius-rounded);
    background: var(--comp-badge-color-background-neutral);
    color: var(--comp-badge-color-text);
    font-weight: var(--global-typography-weight-regular);
    font-style: normal;
    font-size: 12px;
    line-height: 16px;
    transition: 0.3s all cubic-bezier(0.77, 0, 0.18, 1);
  }
</style>

<style lang="scss">
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .modal-filters {
    .ui-modal__body-content-wrapper {
      padding: 0 var(--alias-space-16);
    }

    @include adaptivity.mf-tablet {
      .ui-modal__body-content-wrapper {
        padding: 0 var(--alias-space-24);
      }
    }
  }
</style>
